import { toPng } from 'html-to-image';
import { DEFAULT_SETTLE_DELAY_MS, waitForThumbnailSettle } from './thumbnail-settle';
import { extractHeroImageUrl, fetchAndCropToOg } from './thumbnail-hero';
import { THUMBNAIL_VIEWPORT_H_OG, THUMBNAIL_VIEWPORT_W } from './thumbnail-viewport';

// Re-export the extracted helpers so existing import paths (PageRenderer,
// modules/pages/constants, objects.og/thumbnails tests, thumbnail-capture
// tests) stay byte-identical.
export { sha256Hex } from './thumbnail-hash';
export { prepareThumbnailHtml } from './thumbnail-html';
export { normalizeThumbnailError } from './thumbnail-error';
export { extractHeroImageUrl, fetchAndCropToOg } from './thumbnail-hero';
export { waitForThumbnailSettle } from './thumbnail-settle';
export { THUMBNAIL_VIEWPORT_H_OG, THUMBNAIL_VIEWPORT_W } from './thumbnail-viewport';

/**
 * Walk the iframe document and snap known app intro markers to their
 * final state. The live `PageRenderer` adds intro classes via the
 * `data-xavior-stream-enter` attribute (and removes the attribute when the
 * intro completes); for static capture we just remove the attribute up
 * front so any CSS keyed off it lands at end-state.
 *
 * Idempotent — safe to call multiple times.
 */
function applyXaviorStreamEnterFinalState(doc: Document): void {
  const nodes = doc.querySelectorAll('[data-xavior-stream-enter]');
  nodes.forEach((el) => el.removeAttribute('data-xavior-stream-enter'));
}

/**
 * Rasterize a DOM element to a PNG Blob via html-to-image.
 *
 * `width`/`height` define the source render size (typically the virtual
 * iframe viewport, e.g. 1200×900); `canvasWidth`/`canvasHeight` define the
 * downscaled output dimensions (e.g. 288×216 for a list thumbnail).
 *
 * `skipFonts: true` because trying to inline cross-origin font files
 * routinely fails with CORS and slows the pipeline; the iframe was already
 * painted with those fonts so the rasterizer falls back to the next
 * available font (visually equivalent at thumbnail size).
 */
async function captureElementPng(
  element: HTMLElement,
  opts: {
    width: number;
    height: number;
    canvasWidth: number;
    canvasHeight: number;
  },
): Promise<Blob> {
  const dataUrl = await toPng(element, {
    width: opts.width,
    height: opts.height,
    canvasWidth: opts.canvasWidth,
    canvasHeight: opts.canvasHeight,
    backgroundColor: '#fff',
    pixelRatio: 1,
    skipFonts: true,
    cacheBust: false,
  });
  const response = await fetch(dataUrl);
  return response.blob();
}

/**
 * Capture an element at OG dimensions (1200×630) for use as a published
 * page's `og:image` / `twitter:image`. Reuses the same iframe + settle
 * pipeline as the list-thumbnail capture, but renders at the canonical OG
 * 1.91:1 aspect ratio instead of downscaling to a 288×216 thumb.
 *
 * Caller is responsible for wiring the iframe up: append it to the document
 * with the desired srcdoc + sandbox, and `await` its `load` event before
 * calling this helper. The iframe MUST be set to a 1280×672 layout so the
 * source viewport matches the capture height (otherwise the iframe's
 * layout viewport differs from what we ask the rasterizer to capture and
 * the result includes scrollbars / clipped content).
 *
 * Returns a 1200×630 PNG Blob, or throws on any failure (use
 * `normalizeThumbnailError` to bucket).
 */
export async function captureOgImage(
  iframe: HTMLIFrameElement,
  delayMs: number = DEFAULT_SETTLE_DELAY_MS,
): Promise<Blob> {
  await waitForThumbnailSettle(iframe, delayMs);
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !doc.documentElement) {
    throw new Error('captureOgImage: iframe has no contentDocument');
  }
  applyXaviorStreamEnterFinalState(doc);

  // PRIMARY PATH: extract the hero section's photo URL and use IT as the
  // OG image. This sidesteps every page-rasterisation problem (squish,
  // wrong-portion clipping, navbars/text leaking in, fixed-position
  // descendants) and gives users exactly what they want for shares: just
  // the hero photo, no website chrome.
  //
  // Falls back to page rasterisation only when:
  //   - the page has no hero element with a recognisable image, OR
  //   - the image is on a CORS-blocked host (fetch throws, canvas taints).
  const heroUrl = extractHeroImageUrl(doc);
  if (heroUrl) {
    try {
      const blob = await fetchAndCropToOg(heroUrl);

      console.info('[og-debug] capture: hero image extracted', {
        heroUrl,
        blobBytes: blob.size,
      });
      return blob;
    } catch (err) {

      console.warn('[og-debug] capture: hero-image extraction failed, falling back to page raster', {
        heroUrl,
        reason: err instanceof Error ? err.message : String(err),
      });
      // Fall through to the rasterisation path.
    }
  } else {

    console.info('[og-debug] capture: no hero image found, using page raster fallback');
  }

  // Clip the document to the OG viewport so html-to-image only rasterises
  // the top-of-fold (visible above the OG aspect line). Without this,
  // documentElement reports its full natural page height (often 2000+px),
  // html-to-image captures all of it, and the result gets squished into
  // the 1200×630 OG canvas — visible symptom is "two stacked photos with
  // website text" in iMessage previews.
  //
  // Earlier attempt: inject `<style>html,body{height:672px;overflow:hidden}</style>`
  // into the iframe doc and capture documentElement. This failed in
  // practice (captured the bottom portion of the page instead of the top
  // for users on real pages). Two reasons:
  //   1. html-to-image clones the full element tree at clone-time and
  //      reads getComputedStyle per-node; if the cascade hasn't been
  //      flushed (no layout read between style insertion and clone),
  //      the clip rule may not be picked up.
  //   2. Pages that use flex/grid layouts on body — common for agent-
  //      generated pages that center content vertically — re-position
  //      content within the now-clipped 672 box, sometimes pinning the
  //      "wrong" section to the visible window.
  //
  // Before wrapping, strip site-chrome elements that would otherwise
  // dominate the captured top-of-fold and produce an OG that's
  // mostly logo + nav text instead of the actual page content. The
  // iframe is destroyed right after capture so this is a one-way
  // mutation of the cloned page — no need to restore.
  //
  // Strip:
  //   - <nav> and [role="navigation"] anywhere in the doc
  //   - elements explicitly marked `data-section-id="nav"` (or
  //     "navigation") which agent-generated pages use as a semantic
  //     marker for site navigation
  //   - bare <header> elements that are NOT the hero (hero headers
  //     carry data-section-id="hero" or class*="hero", we keep
  //     those). A pure site <header> is what produces the
  //     "header text on OG" symptom.
  const stripList = doc.querySelectorAll(
    'nav, [role="navigation"], [data-section-id="nav" i], [data-section-id="navigation" i]',
  );
  stripList.forEach((el) => el.remove());
  Array.from(doc.querySelectorAll('header')).forEach((h) => {
    const sid = (h.getAttribute('data-section-id') || '').toLowerCase();
    if (sid === 'hero' || sid === 'hero-section') return;
    if (h.classList.contains('hero')) return;
    if (h.matches('[class*="hero" i]')) return;
    h.remove();
  });

  // The wrapper-div approach below is deterministic regardless of page
  // layout: we move body's children into an absolutely-positioned 1280×672
  // wrapper pinned to top:0, force a layout flush, then capture THE
  // WRAPPER. The wrapper has its own block formatting context, sized
  // exactly to the OG aspect; descendants flow from the top of it; any
  // content beyond 672px is clipped by the wrapper's overflow:hidden.
  // We don't restore the DOM afterwards — the iframe is torn down
  // immediately after capture in PageRenderer.ensurePageOgCapture.
  const wrapper = doc.createElement('div');
  wrapper.setAttribute('data-xavior-og-clip-wrapper', '');
  wrapper.style.cssText = [
    `width: ${THUMBNAIL_VIEWPORT_W}px`,
    `height: ${THUMBNAIL_VIEWPORT_H_OG}px`,
    'overflow: hidden',
    'position: relative',
    'margin: 0',
    'padding: 0',
    // White background so any transparent area in the captured content
    // doesn't show iframe chrome/transparency through to the OG.
    'background: #ffffff',
  ].join(';');

  // Move body's children into the wrapper (preserves layout because the
  // wrapper itself participates in body's block flow at the top).
  while (doc.body.firstChild) {
    wrapper.appendChild(doc.body.firstChild);
  }
  doc.body.appendChild(wrapper);

  // Belt-and-suspenders: also kill body's own min-height/padding so the
  // wrapper sits flush at top:0 with no visible offset. Some pages
  // declare `body { min-height: 100vh }` which would pad-up the body
  // before the wrapper.
  doc.body.style.cssText += ';margin:0 !important;padding:0 !important;min-height:0 !important;';

  // Force a synchronous layout flush so any pending style/layout
  // computation is committed before html-to-image walks the tree. Without
  // this, `getComputedStyle` calls inside the rasteriser can miss recent
  // DOM/style mutations.

  void wrapper.offsetHeight;
  // Ensure the iframe isn't scrolled past the wrapper's top.
  if (win && typeof win.scrollTo === 'function') {
    try { win.scrollTo(0, 0); } catch { /* ignore — sandbox may block */ }
  }

  return captureElementPng(wrapper, {
    width: THUMBNAIL_VIEWPORT_W,
    height: THUMBNAIL_VIEWPORT_H_OG,
    canvasWidth: 1200,
    canvasHeight: 630,
  });
}
